import {get, isEmpty, isFinite, isNil, isPlainObject, merge, toNumber} from 'lodash'
import {isConfirmedQuote, QuoteStatus} from 'helpers/quotes'
import {
  DEFAULT_ORIENTATION,
  DEFAULT_PAGE_SIZE,
  getPrintTemplateGroupRootLabel,
  PageSize,
  PrintTemplateRouteType,
  PrintTemplateStatus,
  PrintTemplateType,
} from 'helpers/printTemplates/enum'
import {PurchaseOrderStatus} from "helpers/purchasing/purchaseOrders"

export const ROOT_PRINT_TEMPLATE_GROUP_ID = 0

export const getRootPrintTemplateGroup = (printTemplateRouteType) => {
  return {
    id: ROOT_PRINT_TEMPLATE_GROUP_ID,
    name: getPrintTemplateGroupRootLabel(printTemplateRouteType),
  }
}

export const getBespokePrintTemplates = (printTemplates) => {
  return printTemplates.filter(isPrintTemplateBespoke)
}

export const getStandardPrintTemplates = (printTemplates) => {
  return printTemplates.filter(printTemplate => {
    return isPrintTemplateNonLegacy(printTemplate) &&
      isPrintTemplateStandard(printTemplate)
  })
}

export const isPrintTemplateLegacy = (printTemplate) => {
  return printTemplate.legacy
}

export const isPrintTemplateNonLegacy = (printTemplate) => {
  return !printTemplate.legacy
}

export const isPrintTemplateStandard = (printTemplate) => {
  return printTemplate.standard
}

export const isPrintTemplateBespoke = (printTemplate) => {
  return !printTemplate.standard
}

export const isPrintTemplatePublished = (printTemplate) => {
  return get(printTemplate, 'status') === PrintTemplateStatus.PUBLISHED
}

export const isPrintTemplateNew = (printTemplate) => {
  return isNil(printTemplate) || isNil(printTemplate.id) || printTemplate.id < 0
}

export const getPrintTemplatePreviewURL = () => {
  return '/service/template/render'
}

const parsePrintTemplateJson = (printTemplate) => {
  const json = get(printTemplate, 'json')

  if (isNil(json) || isEmpty(json))
    return null

  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}

const toDimension = (value) => {
  if (isNil(value) || value === '')
    return null

  const number = toNumber(value)
  return isFinite(number) ? number : null
}

export const getTemplatePageSize = (printTemplate) => {
  const template = parsePrintTemplateJson(printTemplate)
  const pageSize = get(template, 'document.pageSize', DEFAULT_PAGE_SIZE)
  const orientation = get(template, 'document.orientation', DEFAULT_ORIENTATION)

  if (isPlainObject(pageSize))
    return {
      preset: PageSize.CUSTOM,
      isCustom: true,
      width: get(pageSize, 'width', null),
      height: get(pageSize, 'height', null),
      orientation,
    }

  return {
    preset: pageSize,
    isCustom: false,
    width: null,
    height: null,
    orientation,
  }
}


export const setTemplatePageSize = (printTemplate, {preset, width, height, orientation}) => {
  const json = get(printTemplate, 'json')
  const template = parsePrintTemplateJson(printTemplate)

  if (isNil(template))
    return json

  const document = {...get(template, 'document', {})}

  document.pageSize = (preset === PageSize.CUSTOM)
    ? {width: toDimension(width), height: toDimension(height), unit: 'mm'}
    : preset

  document.orientation = orientation

  return JSON.stringify({...template, document}, null, 2)
}

export const getPrintTemplateDataJSON = (data, overrides = {}) => {

  if (isNil(data))
    return ''

  const timeZoneData = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneOffset: new Date().getTimezoneOffset(),
  }

  const dataObject = merge(data, overrides, timeZoneData)
  return JSON.stringify(dataObject)
}

export const getPDFNameFromContext = (printTemplate, context) => {
  if (context.order) return getPDFName(printTemplate, context.order, 'ref')
  if (context.purchaseOrder) return getPDFName(printTemplate, context.purchaseOrder, 'reference')
  if (context.invoice) return getPDFName(printTemplate, context.invoice, 'ref')
  return get(printTemplate, 'name')
}

export const getPDFName = (printTemplate, entity, key) => {
  return isNil(printTemplate) || isNil(entity)
    ? null
    : printTemplate.name + ' ' + entity[key] + (entity.revision ? `-${entity.revision}` : '')
}

export const getDefaultPrintTemplateForType = (printTemplates, printTemplateType) => {
  if (isEmpty(printTemplates)) return null

  const printTemplatesForType = printTemplates.filter(printTemplate => printTemplate.type === printTemplateType)

  const defaultTemplateForType = printTemplatesForType.find(printTemplate => printTemplate.default)

  if (!isNil(defaultTemplateForType)) return defaultTemplateForType

  const quotationPrintTemplate = printTemplatesForType
    .find(printTemplate => printTemplate.name.toLowerCase().includes('quotation'))

  return isNil(quotationPrintTemplate) && !isEmpty(printTemplatesForType)
    ? printTemplatesForType[0]
    : quotationPrintTemplate
}

export const getDefaultPrintTemplateGroupForType = (printTemplateGroups, printTemplateType) => {
  if (isEmpty(printTemplateGroups)) return null
  return printTemplateGroups.find(printTemplateGroup => printTemplateGroup.printTemplateType === printTemplateType)
}

export const getGracePeriodDays = (gracePeriod) => {
  return Math.floor(gracePeriod / 24)
}

export const SignatureType = {
  ORDER: 'ORDER',
  PURCHASE_ORDER: 'PURCHASE_ORDER',
}

export const getSignatureImagePath = (type, id) => {
  switch (type) {
    case SignatureType.ORDER:
      return `/resources/custom/orders/${id}/signature.png`
    case SignatureType.PURCHASE_ORDER:
      return `/resources/custom/purchaseOrders/${id}/signature.png`
  }
}

export const getPublicPDFUrl = (account, user, templateId, date, code, context) => {
  return `/public/${account}/print-template/${templateId}` +
    getPublicSubRouteFromContext(context) +
    `?id=${date}&user=${user}&code=${code}`
}

export const getPublicSubRouteFromContext = (context) => {
  if (context.order)
    return `/order/${context.order}`

  if (context.purchaseOrder)
    return `/purchaseOrder/${context.purchaseOrder}`

  if (context.deliveryNote)
    return `/deliveryNote/${context.deliveryNote}`

  if (context.invoice)
    return `/invoice/${context.invoice}`

  return ''
}

export const getQuotationPrintTemplates = (printTemplates) => {
  if (isNil(printTemplates))
    return null

  return printTemplates.filter(at => at.name.toLowerCase().includes('quotation'))
}

export const getDefaultPrintTemplateIdForOrderStatus = (printTemplates, status) => {
  const printTemplateType = isConfirmedQuote(status)
    ? PrintTemplateType.CONFIRMATION
    : PrintTemplateType.QUOTATION

  const printTemplate = getDefaultPrintTemplateForType(printTemplates, printTemplateType)
  return get(printTemplate, 'id', null)
}

export const getDefaultPrintTemplateForQuotes = (printTemplates, status) => {
  const printTemplateType = status === QuoteStatus.draft
    ? PrintTemplateType.DRAFT
    : PrintTemplateType.QUOTATION

  return getDefaultPrintTemplateForType(printTemplates, printTemplateType)
}

export const getDefaultPrintTemplateForPurchaseOrders = (printTemplates, status) => {
  const printTemplateType = status === PurchaseOrderStatus.draft
    ? PrintTemplateType.DRAFT_PURCHASE_ORDER
    : PrintTemplateType.PURCHASE_ORDER

  return getDefaultPrintTemplateForType(printTemplates, printTemplateType)
}

export const getDefaultPrintTemplateForDeliveries = (printTemplates, isReturnNote) => {
  const printTemplateType = isReturnNote
    ? PrintTemplateType.RETURN_NOTE
    : PrintTemplateType.DELIVERY_NOTE

  return getDefaultPrintTemplateForType(printTemplates, printTemplateType)
}

export const getDefaultPrintTemplateForInvoices = (printTemplates, isCreditNote) => {
  const printTemplateType = isCreditNote
    ? PrintTemplateType.CREDIT_NOTE
    : PrintTemplateType.INVOICE

  return getDefaultPrintTemplateForType(printTemplates, printTemplateType)
}

export const getSelectedPrintTemplate = (printTemplates, selectedPrintTemplateId, defaultPrintTemplate) => {
  if (isEmpty(printTemplates)) {
    return null
  }
  const selectedPrintTemplate = printTemplates.find(template => template.id === selectedPrintTemplateId)
  if (!isNil(selectedPrintTemplate)) {
    return selectedPrintTemplate
  }
  return !isNil(defaultPrintTemplate) ? defaultPrintTemplate : printTemplates[0]
}

export const updatePrintOptions = (entity, entityIdKey, key, value) => {
  const {printOptions} = entity
  return isNil(printOptions)
    ? {
      [entityIdKey]: entity.id,
      [key]: value,
      createdDate: new Date().getTime(),
      modifiedDate: new Date().getTime(),
    }
    : {
      ...printOptions,
      modifiedDate: new Date().getTime(),
      [key]: value,
    }
}

export const getContext = (routeType, orderId, purchaseOrderId, deliveryNoteId, invoiceId) => {
  switch (routeType) {
    case PrintTemplateRouteType.QUOTING:
      return {orderId}
    case PrintTemplateRouteType.PURCHASING:
      return {purchaseOrderId}
    case PrintTemplateRouteType.DELIVERIES:
      return {deliveryNoteId}
    case PrintTemplateRouteType.INVOICING:
      return {invoiceId}
    default:
      return {}
  }
}

export const getContextFromObject = (routeType, order, purchaseOrder, deliveryNote, invoice) => {
  switch (routeType) {
    case PrintTemplateRouteType.QUOTING:
      return {orderId: get(order, 'id')}
    case PrintTemplateRouteType.PURCHASING:
      return {purchaseOrderId: get(purchaseOrder, 'id')}
    case PrintTemplateRouteType.DELIVERIES:
      return {deliveryNoteId: get(deliveryNote, 'id')}
    case PrintTemplateRouteType.INVOICING:
      return {invoiceId: get(invoice, 'id')}
    default:
      return {}
  }
}

export const getContextObject = (routeType, order, purchaseOrder, deliveryNote, invoice) => {
  switch (routeType) {
    case PrintTemplateRouteType.QUOTING:
      return order
    case PrintTemplateRouteType.PURCHASING:
      return purchaseOrder
    case PrintTemplateRouteType.DELIVERIES:
      return deliveryNote
    case PrintTemplateRouteType.INVOICING:
      return invoice
    default:
      return null
  }
}

export const isContextValid = (context) => {
  return (
    !isNil(context.orderId) ||
    !isNil(context.purchaseOrderId) ||
    !isNil(context.deliveryNoteId) ||
    !isNil(context.invoiceId)
  )
}

export const getNewPrintTemplate = (name, printTemplateType, printTemplateGroupId) => {
  return {
    id: -1,
    name,
    type: printTemplateType,
    status: PrintTemplateStatus.DRAFT,
    printTemplateGroupId,
    legacy: false,
    standard: false,
    createdDate: new Date().getTime(),
    modifiedDate: new Date().getTime(),
  }
}

export const isSignAcceptanceEnabled = (printTemplate) => {
  switch (printTemplate.type) {
    case PrintTemplateType.DRAFT:
      return false
    case  PrintTemplateType.DRAFT_PURCHASE_ORDER:
      return false
    default:
      return true
  }
}

export const getPrintTemplateFileName = (context) => {

  const templateName = get(context, 'printTemplate.name', null)
  if (context.order) {

    return `${templateName} ${context.order.ref}.pdf`
  }

  if (context.purchaseOrder)
    return `${templateName} ${context.purchaseOrder.reference}.pdf`

  if (context.deliveryNote)
    return context.deliveryNote.returnNote
      ? `${templateName} ${context.deliveryNote.reference}.pdf`
      : `${templateName} ${context.deliveryNote.reference}.pdf`

  if (context.invoice)
    return context.invoice.creditNote
      ? `${templateName} ${context.invoice.ref}.pdf`
      : `${templateName} ${context.invoice.ref}.pdf`

  return ''
}

